import React from 'react';
import { PrimeIndemnite } from '../types';
import PrimeCard from './ui/PrimeCard';
import EditableAmount from './ui/EditableAmount';
import { calculateTotalMonthlyPrimes } from '../utils/primeCalculations';

interface PrimesListProps {
  primesWithCalculatedAmounts: PrimeIndemnite[];
  workTimePercentage: number;
  selectedStatus?: StatusType;
  selectedGrade?: number | null;
  selectedEchelon?: number;
  editingPrime: string | null;
  editingBaseAmount: string | null;
  editingReimbursementRate: string | null;
  expandedPrimes: Set<string>;
  selectedZoneIR: number | null;
  currentIndiceMajore: number | null;
  onTogglePrime: (primeId: string) => void;
  onToggleDetails: (primeId: string) => void;
  onEditAmount: (primeId: string) => void;
  onSaveAmount: (primeId: string, amount: number) => boolean;
  onCancelEditAmount: () => void;
  onEditBaseAmount: (primeId: string) => void;
  onSaveBaseAmount: (primeId: string, amount: number) => boolean;
  onCancelEditBaseAmount: () => void;
  onEditReimbursementRate: (primeId: string) => void;
  onSaveReimbursementRate: (primeId: string, rate: number) => boolean;
  onCancelEditReimbursementRate: () => void;
  onZoneChange: (zone: number) => void;
  onIFTDConfigChange: (primeId: string, config: { dimanchesParMois?: number; joursFeriesTravailles?: string[] }) => void;
  onContagionConfigChange: (primeId: string, config: { joursTravaillesParAn?: number }) => void;
  onPrimeNuitConfigChange: (primeId: string, config: { nuitsParMois?: number }) => void;
  onSFTConfigChange: (primeId: string, config: { nombreEnfants?: number }) => void;
  onTransportBaseAmountChange: (primeId: string, amount: number) => void;
  onInsalubriteCat2ConfigChange: (primeId: string, config: { joursTravaillesParMois?: number }) => void;
  onISSConfigChange: (primeId: string, config: { echelonHistorique?: number; indiceMajoreHistorique?: number }) => void;
  onNBIConfigChange: (primeId: string, config: { points?: number }) => void;
  onAmountChange: (primeId: string, amount: number) => void;
  getDocumentationUrl: (primeId: string) => string | undefined;
  onPrimesChange: (primesData: { primes: PrimeIndemnite[]; totalMensuel: number }) => void;
  onMarkPrimesModified: () => void;
  expandedCalculationDetails: Set<string>;
  onToggleCalculationDetails: (primeId: string) => void;
  professionId?: string;
}

export default function PrimesList({
  primesWithCalculatedAmounts,
  workTimePercentage,
  selectedStatus,
  selectedGrade,
  selectedEchelon,
  editingPrime,
  editingBaseAmount,
  editingReimbursementRate,
  expandedPrimes,
  selectedZoneIR,
  currentIndiceMajore,
  onTogglePrime,
  onToggleDetails,
  onEditAmount,
  onSaveAmount,
  onCancelEditAmount,
  onEditBaseAmount,
  onSaveBaseAmount,
  onCancelEditBaseAmount,
  onEditReimbursementRate,
  onSaveReimbursementRate,
  onCancelEditReimbursementRate,
  onZoneChange,
  onIFTDConfigChange,
  onContagionConfigChange,
  onPrimeNuitConfigChange,
  onSFTConfigChange,
  onTransportBaseAmountChange,
  onInsalubriteCat2ConfigChange,
  onISSConfigChange,
  onNBIConfigChange,
  onAmountChange,
  getDocumentationUrl,
  onPrimesChange,
  onMarkPrimesModified,
  expandedCalculationDetails,
  onToggleCalculationDetails,
  professionId
}: PrimesListProps) {
  return (
    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
      <h3 className="font-medium text-gray-800 text-sm">
        Configurez vos primes et indemnités :
      </h3>
      
      {primesWithCalculatedAmounts.map((prime) => {
        const isEditingAmount = editingPrime === prime.id;
        const isEditingBase = editingBaseAmount === prime.id;
        const isEditingRate = editingReimbursementRate === prime.id;
        
        return (
          <div key={prime.id} className="space-y-2">
            {/* Mode édition du montant */}
            {isEditingAmount && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-blue-800">
                    Modifier le montant - {prime.nom}
                  </span>
                </div>
                <EditableAmount
                  value={prime.montantUtilisateur}
                  onSave={(amount) => onSaveAmount(prime.id, amount)}
                  onCancel={onCancelEditAmount}
                  suffix="€"
                  fieldName={`montant de la prime ${prime.nom}`}
                />
              </div>
            )}

            {/* Mode édition du montant de base */}
            {isEditingBase && (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-orange-800">
                    Modifier le montant de base - {prime.nom}
                  </span>
                </div>
                <EditableAmount
                  value={prime.montantBase || 0}
                  onSave={(amount) => onSaveBaseAmount(prime.id, amount)}
                  onCancel={onCancelEditBaseAmount}
                  suffix="€"
                  fieldName="montant de base"
                />
              </div>
            )}

            {/* Mode édition du taux de remboursement */}
            {isEditingRate && (
              <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-teal-800">
                    Modifier le taux de remboursement - {prime.nom}
                  </span>
                </div>
                <EditableAmount
                  value={prime.pourcentageRemboursement || 0}
                  onSave={(rate) => onSaveReimbursementRate(prime.id, rate)}
                  onCancel={onCancelEditReimbursementRate}
                  suffix="%"
                  fieldName="taux de remboursement"
                  max={100}
                />
              </div>
            )}

            {/* Carte de la prime */}
            {!isEditingAmount && !isEditingBase && !isEditingRate && (
              <div>
                <PrimeCard
                  prime={prime}
                  workTimePercentage={workTimePercentage}
                  selectedStatus={selectedStatus}
                  selectedGrade={selectedGrade}
                  selectedEchelon={selectedEchelon}
                  onToggle={() => onTogglePrime(prime.id)}
                  onEditBaseAmount={prime.calculable ? () => onEditBaseAmount(prime.id) : undefined}
                  onEditReimbursementRate={prime.calculable ? () => onEditReimbursementRate(prime.id) : undefined}
                  onZoneChange={prime.id === 'indemnite_residence' ? onZoneChange : undefined}
                  selectedZone={prime.id === 'indemnite_residence' ? selectedZoneIR : undefined}
                  documentationUrl={getDocumentationUrl(prime.id)}
                  showDetails={expandedPrimes.has(prime.id)}
                  onToggleDetails={() => onToggleDetails(prime.id)}
                  currentIndiceMajore={currentIndiceMajore}
                  onIFTDConfigChange={prime.id === 'iftd' ? (config) => onIFTDConfigChange(prime.id, config) : undefined}
                  onContagionConfigChange={prime.id === 'contagion' ? (config) => onContagionConfigChange(prime.id, config) : undefined}
                  onPrimeNuitConfigChange={prime.id === 'prime_nuit' ? (config) => onPrimeNuitConfigChange(prime.id, config) : undefined}
                  onSFTConfigChange={prime.id === 'sft' ? (config) => onSFTConfigChange(prime.id, config) : undefined}
                  onTransportBaseAmountChange={prime.id === 'transport' ? onTransportBaseAmountChange : undefined}
                  onInsalubriteCat2ConfigChange={prime.id === 'insalubrite' ? (config) => {
                    console.log('🔧 PrimesList - onInsalubriteCat2ConfigChange called for prime:', prime.id);
                    console.log('🔧 PrimesList - config:', config);
                    onInsalubriteCat2ConfigChange(prime.id, config);
                  } : undefined}
                  onNBIConfigChange={prime.id === 'nbi' ? (config) => onNBIConfigChange(prime.id, config) : undefined}
                  onISSConfigChange={prime.id === 'iss' ? (config) => onISSConfigChange(prime.id, config) : undefined}
                  onAmountChange={(amount) => {
                    const updatedPrimes = primesWithCalculatedAmounts.map(p => {
                      if (p.id === prime.id) {
                        // Pour la prime transport, on met à jour aussi le montant de base si nécessaire
                        if (p.id === 'transport' && p.montantBase && p.pourcentageRemboursement) {
                          // Si on modifie le montant final, recalculer le montant de base
                          const newBaseAmount = amount / (p.pourcentageRemboursement / 100);
                          return { ...p, montantUtilisateur: amount, montantBase: newBaseAmount };
                        }
                        return { ...p, montantUtilisateur: amount };
                      }
                      return p;
                    });
                    
                    const newPrimesData = {
                      primes: updatedPrimes,
                      totalMensuel: calculateTotalMonthlyPrimes(updatedPrimes, workTimePercentage)
                    };
                    
                    onPrimesChange(newPrimesData);
                    onMarkPrimesModified();
                    return true; // Return success indicator
                  }}
                  isEditingAmount={editingPrime === prime.id}
                  onEditAmount={prime.type === 'variable' && (!prime.calculable || prime.isMaxCalculated) ? () => onEditAmount(prime.id) : undefined}
                  showCalculationDetails={expandedCalculationDetails.has(prime.id)}
                  onToggleCalculationDetails={() => onToggleCalculationDetails(prime.id)}
                  professionId={professionId}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}